/*
 * File: OcdCell.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 29 Dec 2021 3:41:14 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Divider, Form, Select, Tooltip } from 'antd';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderLineItemModel } from 'models/SalesOrderLineItemModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setOcdValues } from 'redux/productsInfo/productsInfoSlice';
import { ReduxState } from 'redux/rootReducer';
import { useGetReleasedProductQuery } from 'redux/services/useDonaldGrumpService';
import AddOcdModal from './AddOcdModal';

interface OcdCellProps {
  ownerId: SalesOrderPayloadModel['lines'][number]['ownerId'];
  conditionId: SalesOrderPayloadModel['lines'][number]['conditionId'];
  dispositionId: SalesOrderPayloadModel['lines'][number]['dispositionId'];
  productNumber: SalesOrderPayloadModel['lines'][number]['productNumber'];
  id: SalesOrderPayloadModel['lines'][number]['id'];
  index: number;
  record: SalesOrderLineItemModel;
}

const styles: InlineStylesModel = {
  space: {
    width: '100%',
    padding: 10
  },
  select: {
    width: '100%'
  },
  formContainer: {
    paddingRight: 10,
    paddingLeft: 10
  },
  formItem: {
    marginBottom: 0
  },
  ocdButton: {
    width: '100%'
  }
};

export const OcdCell = React.memo(({ record, index, conditionId, dispositionId, ownerId, id, productNumber }: OcdCellProps) => {
  const form = Form.useFormInstance();
  const dispatch = useDispatch();

  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { ocdValues } = useSelector((state: ReduxState) => state.productsInfo);

  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  const {
    data: releasedProductData,
    isLoading,
    isFetching,
    isError,
    error
  } = useGetReleasedProductQuery({ productNumber: record.productNumber, dataAreaId: selectedCompanyDataAreaId as string }, { skip: !selectedCompanyDataAreaId });

  useEffect(() => {
    dispatch(setOcdValues(`${ownerId} :: ${conditionId} :: ${dispositionId}`));
  }, [conditionId, dispositionId, ownerId]);

  /* ******************** JSX Variables ******************** */
  const recordOcd = ownerId && conditionId && dispositionId ? `${ownerId} :: ${conditionId} :: ${dispositionId}`.toUpperCase() : undefined;

  const sortedReleasedProducts = [...(releasedProductData?.releasedProductVariants ?? [])];

  if (sortedReleasedProducts && sortedReleasedProducts.length > 1) sortedReleasedProducts.sort((a, b) => (a.ownerId < b.ownerId ? -1 : 1));

  const options = sortedReleasedProducts?.map((variant) => ({
    label: `${variant.ownerId} :: ${variant.conditionId} :: ${variant.dispositionId}`.toUpperCase(),
    value: `${variant.ownerId} :: ${variant.conditionId} :: ${variant.dispositionId}`.toUpperCase()
  }));

  const isInvalid = (): boolean => {
    if ((id.includes('import') && !recordOcd) || (!recordOcd && pathname.includes('/drafts/'))) {
      return true;
    } else {
      return false;
    }
  };

  /* ******************** Functions ******************** */
  const handleOcdSelect = (value: string): void => {
    const ownerId = value.split(' :: ')[0];
    const conditionId = value.split(' :: ')[1];
    const dispositionId = value.split(' :: ')[2];

    dispatch(setOcdValues(`${ownerId} :: ${conditionId} :: ${dispositionId}`));
    form.setFieldValue('ocd', `${ownerId} :: ${conditionId} :: ${dispositionId}`);
  };

  const dropdownRenderJSX = (menu: any): JSX.Element => (
    <>
      {menu}
      <Divider style={{ margin: '4px 0' }} />
      <div style={styles.formContainer}>
        <AddOcdModal productNumber={productNumber} index={index} isError={isError} />
      </div>
    </>
  );

  /* ****************** Render ****************** */
  return (
    <Tooltip title={isError && !isLoading ? ((error as FetchBaseQueryError).data as string) : ''}>
      <Select
        disabled={isPosted || isError}
        dropdownMatchSelectWidth={false}
        dropdownRender={dropdownRenderJSX}
        onSelect={handleOcdSelect}
        options={options}
        showSearch
        style={styles.select}
        status={isInvalid() ? 'error' : undefined}
        value={ocdValues.length > 8 ? ocdValues : ''}
      />
    </Tooltip>
  );
});
